﻿import React from 'react';
import { FormField } from '../inputs/FormField';
import { TextInput } from '../inputs/TextInput';
import { TextareaInput } from '../inputs/TextareaInput';
import { SelectInput, SelectOption } from '../inputs/SelectInput';
import { CheckboxInput } from '../inputs/CheckboxInput';
import { CommonFieldProps } from './CommonFieldProps';

interface CourierFieldsProps extends CommonFieldProps {
    retailerOptions: SelectOption[];
    localeOptions: SelectOption[];
    courierOptions: SelectOption[];
    loadingCouriers: boolean;
}

export const CourierFields: React.FC<CourierFieldsProps> = ({ 
    formik, 
    getFieldError, 
    hasError, 
    retailerOptions, 
    localeOptions, 
    courierOptions, 
    loadingCouriers 
}) => {
    const selectedRetailerOption = retailerOptions.find(opt => opt.value === formik.values.selectedRetailerId) || null;
    const selectedCourierLocaleOption = localeOptions.find(opt => opt.value === formik.values.courierLocaleCode) || null;
    const selectedCourierOption = courierOptions.find(opt => opt.value === formik.values.selectedCourierId) || null;

    // Determine if we should hide the locale dropdown.
    // We hide it if a retailer is selected and that retailer has an associated locale,
    // which results in courierLocaleCode being automatically set.
    const shouldHideLocale = !!(formik.values.selectedRetailerId && formik.values.courierLocaleCode);

    return (
        <div>
            <h2 className="h4 fw-bold mb-4 text-dark">Courier Details</h2>
            
            <FormField
                label="Store"
                required
                helpText="Which store uses this courier?"
                htmlFor="selectedRetailerId"
                error={getFieldError('selectedRetailerId')}
            >
                <SelectInput
                    inputId="selectedRetailerId"
                    value={selectedRetailerOption}
                    onChange={(option: SelectOption | null) => formik.setFieldValue('selectedRetailerId', option?.value || '')}
                    options={retailerOptions}
                    placeholder="Select a store"
                    isClearable
                    isSearchable
                    hasError={hasError('selectedRetailerId')}
                />
            </FormField>

            {formik.values.selectedRetailerId && (
                <div>
                    {!shouldHideLocale && (
                        <FormField
                            label="Country / Region"
                            required
                            htmlFor="courierLocaleCode"
                            error={getFieldError('courierLocaleCode')}
                        >
                            <SelectInput
                                inputId="courierLocaleCode"
                                value={selectedCourierLocaleOption}
                                onChange={(option: SelectOption | null) => formik.setFieldValue('courierLocaleCode', option?.value || '')}
                                options={localeOptions}
                                placeholder="Select a country"
                                isClearable
                                isSearchable
                                hasError={hasError('courierLocaleCode')}
                            />
                        </FormField>
                    )}

                    <div className="mb-4">
                        <CheckboxInput
                            id="isNewCourier"
                            name="isNewCourier"
                            checked={formik.values.isNewCourier}
                            onChange={formik.handleChange}
                            label="Add a new courier"
                            description="Check this if the courier isn't in the list above"
                        />
                    </div>

                    {formik.values.isNewCourier ? (
                        <>
                            <FormField
                                label="Courier Name"
                                required
                                helpText="e.g. Royal Mail, DHL, FedEx"
                                htmlFor="courierName"
                                error={getFieldError('courierName')}
                            >
                                <TextInput
                                    id="courierName"
                                    name="courierName"
                                    value={formik.values.courierName}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    placeholder="Enter courier name"
                                    required={formik.values.isNewCourier}
                                    hasError={hasError('courierName')}
                                />
                            </FormField>
                            <FormField
                                label="Courier Website"
                                helpText="The courier's website (optional)"
                                htmlFor="courierUrl"
                                error={getFieldError('courierUrl')}
                            >
                                <TextInput
                                    id="courierUrl"
                                    name="courierUrl"
                                    type="url"
                                    value={formik.values.courierUrl}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    placeholder="https://example.com"
                                    hasError={hasError('courierUrl')}
                                />
                            </FormField>
                        </>
                    ) : (
                        <FormField
                            label="Select Courier"
                            required
                            helpText="Pick the courier from the list"
                            htmlFor="selectedCourierId"
                            error={getFieldError('selectedCourierId')}
                        >
                            <SelectInput
                                inputId="selectedCourierId"
                                value={selectedCourierOption}
                                onChange={(option: SelectOption | null) => formik.setFieldValue('selectedCourierId', option?.value || '')}
                                options={courierOptions}
                                placeholder="Select a courier"
                                isClearable
                                isSearchable
                                isDisabled={loadingCouriers}
                                hasError={hasError('selectedCourierId')}
                            />
                        </FormField>
                    )}

                    <FormField
                        label="Anything else to add?"
                        helpText="e.g. they only use this for large items (optional)"
                        htmlFor="courierComments"
                        error={getFieldError('courierComments')}
                    >
                        <TextareaInput
                            id="courierComments"
                            name="courierComments"
                            value={formik.values.courierComments}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            rows={3}
                            placeholder="Add any extra details here..."
                            hasError={hasError('courierComments')}
                        />
                    </FormField>
                </div>
            )}
        </div>
    );
};
